import { DateTime } from 'luxon';

export const isWeekendDate = (date: Date) => {
  return isWeekendDateTime(DateTime.fromJSDate(date));
};

export const isWeekendDateTime = (dateTime: DateTime) => {
  return dateTime.weekday > 5;
};

export const getNextBusinessDateTime = (): DateTime => {
  const dateTime = DateTime.utc().startOf('day');
  if (dateTime.weekday === 5 || dateTime.weekday === 6) {
    return dateTime.plus({days: 8 - dateTime.weekday});
  } else {
    return dateTime.plus({days: 1});
  }
};

export const getNextBusinessDay = (): Date => {
  return getNextBusinessDateTime().toJSDate();
};

export const isValidDate = (date: Date) => {
  return date instanceof Date && !isNaN(date.getDate());
};

export const getDateFromString = (dateString: string, format = 'yyyy-LL-dd'): Date =>
    DateTime.fromFormat(dateString, format).setZone('UTC').toJSDate();

export const getDateFromStringOrUndefined = (dateString?: string, format = 'yyyy-LL-dd'): Date | undefined =>
    dateString != null && dateString.length > 0 ? getDateFromString(dateString, format) : undefined;

export const getDateTimeFromString = (dateString: string, format = 'yyyy-LL-dd'): DateTime => {
  return DateTime.fromFormat(dateString, format);
};

export const getFullYearsBetweenDateAndToday = (date: Date): number => {
  return Math.floor(Math.abs(DateTime.fromJSDate(date).diffNow('years').years));
};

export const addDaysToDate = (days: number, date: Date): Date => {
  const dateTime = DateTime.fromJSDate(date);
  return dateTime.plus({days}).startOf('day').toJSDate();
};

export const addYearsToDate = (years: number, date: Date): Date => {
  const dateTime = DateTime.fromJSDate(date);
  return dateTime.plus({years}).startOf('day').toJSDate();
};

export const isDateGreaterOrEqualByDay = (date1: Date, date2: Date): boolean => {
  const dateTime1 = DateTime.fromJSDate(date1);
  const dateTime2 = DateTime.fromJSDate(date2);
  return dateTime1.startOf('day') >= dateTime2.startOf('day');
};

export const isDateLessOrEqualByDay = (date1: Date, date2: Date): boolean => {
  const dateTime1 = DateTime.fromJSDate(date1);
  const dateTime2 = DateTime.fromJSDate(date2);
  return dateTime1.startOf('day') <= dateTime2.startOf('day');
};

export const getNow = (): Date => DateTime.local().toJSDate();

const HOLIDAYS = [
  '01/01',
  '06/01',
  '01/05',
  '03/05',
  '15/08',
  '01/11',
  '11/11',
  '24/12',
  '25/12',
  '26/12',
  '31/12'
];

export const isHolidayDate = (date: Date, holidays = HOLIDAYS) => {
  const dateTime = DateTime.fromJSDate(date);
  return holidays.includes(dateTime.toFormat('dd/MM'));
};

export const isLegalAge = (date: string, { legalAge = 18, format='dd.MM.yyyy' } = {}): boolean => {
  return DateTime.fromFormat(date, format)
                 .diffNow('years')
                 .years < (Math.abs(legalAge) * -1);
};

export const isStringDateAfterFormatValid = (date: string, format = 'yyyy-MM-dd'): boolean => {
  if (date === null || date === undefined) {
    return false;
  }
  return DateTime.fromISO(DateTime.fromFormat(date, format).toString()).isValid;
}

export function formatIsoToStandardDate(dateString: string): string {
  return DateTime.fromFormat(dateString, 'yyyy-MM-dd').toFormat('dd.MM.yyyy');
}

export function createJsDateWithMonth(year: number, month: number, day: number): Date {
  return new Date(year, month - 1, day);
}

export function isDateBetweenDates(date: Date, startDate: Date, endDate: Date): boolean {
  return date >= startDate && date <= endDate;
}

export const isDateEqualByDay = (date1: Date, date2: Date): boolean => date1.toDateString() === date2.toDateString();

export const getCurrentYear = () => DateTime.local().year;
export const getCurrentMonth = () => DateTime.local().month;

export const getDaysBetweenTodayAndDate = (otherDate: Date): number => {
  const today = DateTime.local().startOf('day');
  const otherDateTime = DateTime.fromJSDate(otherDate).startOf('day');
  return Math.abs(otherDateTime.diff(today, 'days').days);
};
